import Axios from '@/utils/axios';
import { getBackendErrorMessage } from '@/utils/errorHandler';

/**
 * Shipment-export template config (nest-logistic-service):
 *   GET    /v1/export-templates                      registered layouts
 *   GET    /v1/client-export-templates               template -> client owners
 *   PUT    /v1/client-export-templates/:templateKey  assign a template's client
 *   DELETE /v1/client-export-templates/:templateKey  make a template global
 *
 * Each template is owned by at most one client (or global with no row); a client
 * can own several. A template with no owner is available to every client. See
 * PRD-DYNAMIC-SHIPMENT-EXPORT.
 */

/** A registered export layout, for the template picker. */
export interface ExportTemplateSummary {
  key: string;
}

/** One client -> template mapping row. */
export interface ClientExportTemplateMapping {
  id: string;
  clientId: number;
  templateKey: string;
  createdBy: string;
  createdAt: string;
  updatedBy: string;
  updatedAt: string;
}

type Envelope<T> = { status: string | number; message?: string; data: T };

export const exportTemplatesAPI = {
  listTemplates: async (): Promise<ExportTemplateSummary[]> => {
    try {
      const res = await Axios.get<Envelope<{ templates: ExportTemplateSummary[] }>>(
        '/v1/export-templates'
      );
      return res.data?.data?.templates ?? [];
    } catch (error) {
      throw new Error(getBackendErrorMessage(error));
    }
  },

  listMappings: async (): Promise<ClientExportTemplateMapping[]> => {
    try {
      const res = await Axios.get<
        Envelope<{ data: ClientExportTemplateMapping[] }>
      >('/v1/client-export-templates');
      return res.data?.data?.data ?? [];
    } catch (error) {
      throw new Error(getBackendErrorMessage(error));
    }
  },

  /** Assign the client that owns a template (upsert by template key). */
  upsertMapping: async (
    templateKey: string,
    clientId: number
  ): Promise<ClientExportTemplateMapping> => {
    try {
      const res = await Axios.put<Envelope<ClientExportTemplateMapping>>(
        `/v1/client-export-templates/${encodeURIComponent(templateKey)}`,
        { clientId }
      );
      return res.data.data;
    } catch (error) {
      throw new Error(getBackendErrorMessage(error));
    }
  },

  /** Clear a template's owner (make it global / available to all clients). */
  deleteMapping: async (templateKey: string): Promise<void> => {
    try {
      await Axios.delete(
        `/v1/client-export-templates/${encodeURIComponent(templateKey)}`
      );
    } catch (error) {
      throw new Error(getBackendErrorMessage(error));
    }
  },
};
